import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Trophy, TrendingUp, Users, Clock, Award, Share2, 
  ChevronDown, ChevronUp, CheckCircle2, Target, Zap,
  Star, Building2, GraduationCap, ArrowRight, X,
  Sparkles, Medal, Crown, Flame, ArrowUpRight,
  MessageCircle, Copy, Check, PlayCircle, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// CONFIGURATION
// ============================================
const QUALIFICATION_THRESHOLD = 50;
const CAMPAIGN_SOURCE = 'dcri2026';

// ============================================
// MOCK DATA - Will be replaced with API
// ============================================
const LIVE_ACTIVITY_FEED = [
  { college: "Graphic Era University", action: "just completed the check", time: "2 mins ago" },
  { college: "UPES", action: "reached 45 students", time: "5 mins ago" },
  { college: "DIT University", action: "climbed to #4", time: "8 mins ago" },
  { college: "IMS Unison", action: "just completed the check", time: "12 mins ago" },
  { college: "Doon Business School", action: "reached 30 students", time: "15 mins ago" },
];

const LEADERBOARD_DATA = {
  qualified: [],
  challengers: [
    { rank: 1, college: "Graphic Era University", index: "—", students: 42, movement: "hot", progress: 84 },
    { rank: 2, college: "UPES", index: "—", students: 38, movement: "up", progress: 76 },
    { rank: 3, college: "DIT University", index: "—", students: 35, movement: "up", progress: 70 },
    { rank: 4, college: "IMS Unison University", index: "—", students: 28, movement: "new", progress: 56 },
    { rank: 5, college: "Doon Business School", index: "—", students: 22, movement: "up", progress: 44 },
  ]
};

// ============================================
// FAQ DATA
// ============================================
const faqData = [
  {
    question: "Who can participate?",
    answer: "Any student currently enrolled in a college or university in Dehradun can participate. Recent graduates (within 1 year) are also welcome."
  },
  {
    question: "How is Career Readiness measured?",
    answer: "The Career Readiness Check evaluates four key dimensions: Career Direction, Profile Strength, Interview Readiness, and Job Search Strategy."
  },
  {
    question: "How does a college qualify for the leaderboard?",
    answer: `A college needs at least ${QUALIFICATION_THRESHOLD} valid student responses to appear on the official ranked leaderboard.`
  },
  {
    question: "Can students submit more than once?",
    answer: "Each student can submit only once. Duplicate submissions are automatically filtered."
  },
  {
    question: "How are student details used?",
    answer: "Your data is used solely for the Index. We do not share individual responses with colleges."
  },
  {
    question: "When will rankings be finalized?",
    answer: "The Index runs until March 31, 2026. Final rankings announced in April 2026."
  },
];

// ============================================
// GA4 TRACKING
// ============================================
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// ============================================
// LIVE TICKER COMPONENT
// ============================================
const LiveActivityTicker = () => {
  return (
    <div className="bg-slate-900 border-y border-amber-500/30 overflow-hidden py-2">
      <div className="flex animate-marquee whitespace-nowrap">
        {[...LIVE_ACTIVITY_FEED, ...LIVE_ACTIVITY_FEED].map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 mx-8 text-sm">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-semibold">{item.college}</span>
            <span className="text-slate-400">{item.action}</span>
            <span className="text-slate-500">• {item.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// MOVEMENT BADGE COMPONENT
// ============================================
const MovementBadge = ({ type }) => {
  if (type === 'hot') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-bold">
      <Flame className="w-3 h-3" /> HOT
    </span>
  );
  if (type === 'up') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold">
      <TrendingUp className="w-3 h-3" /> ↑
    </span>
  );
  if (type === 'new') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400 text-xs font-bold">
      NEW
    </span>
  );
  return null;
};

// ============================================
// MAIN COMPONENT
// ============================================
export const Dehradun = () => {
  const [searchParams] = useSearchParams();
  const [showCampusChampionModal, setShowCampusChampionModal] = useState(false);
  const [showCollegePartnerModal, setShowCollegePartnerModal] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [leaderboardTab, setLeaderboardTab] = useState('challengers');
  const [totalParticipants, setTotalParticipants] = useState(1247);
  
  const leaderboardRef = useRef(null);

  // UTM params preservation
  const utmParams = {
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    ref: searchParams.get('ref') || '',
  };

  useEffect(() => {
    document.title = "Dehradun Career Readiness Index 2026 | Elevate Careers";
    trackEvent('dcri_page_view');
    
    // Simulate live counter
    const interval = setInterval(() => {
      setTotalParticipants(prev => prev + Math.floor(Math.random() * 2));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleRepresentClick = (source = 'hero') => {
    trackEvent('represent_college_click', { source });
    const params = new URLSearchParams();
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    window.location.href = `/dehradun/check${params.toString() ? '?' + params.toString() : ''}`;
  };

  const scrollToLeaderboard = () => {
    trackEvent('leaderboard_view');
    leaderboardRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ============================================
          HEADER
          ============================================ */}
      <header className="py-3 px-4 bg-slate-950/95 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="bg-white px-2.5 py-1.5 rounded-lg">
              <img src="/logo-elevate-careers.webp" alt="Elevate Careers" className="h-6 sm:h-7 w-auto" />
            </div>
            <div className="hidden sm:block">
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">DCRI 2026</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-slate-400">
                <span className="text-white font-bold">{totalParticipants.toLocaleString()}</span> students checked
              </span>
            </div>
            <Button 
              onClick={() => handleRepresentClick('header')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-4 sm:px-5 py-2 text-sm font-bold"
              data-testid="header-cta-btn"
            >
              Represent College
            </Button>
          </div>
        </div>
      </header>

      {/* ============================================
          LIVE ACTIVITY TICKER
          ============================================ */}
      <LiveActivityTicker />

      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative bg-gradient-to-br from-slate-950 via-[#0b1f4d] to-slate-900 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full blur-[128px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500 rounded-full blur-[150px]" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 pt-10 sm:pt-14 pb-8 sm:pb-12">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              {/* Campaign Badge */}
              <div className="flex justify-center lg:justify-start mb-5">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-300 px-4 py-2 rounded-full">
                  <Trophy className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-bold tracking-wide">DEHRADUN CAREER READINESS INDEX 2026</span>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 leading-[1.1] tracking-tight">
                Which College Has the Most{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500">
                  Career-Ready
                </span>{' '}
                Students?
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-slate-300 mb-6 max-w-lg mx-auto lg:mx-0">
                Take the 3-minute check. Get your personal Career Readiness Score. 
                Help your college climb the official Dehradun Index.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <Button 
                  onClick={() => handleRepresentClick('hero')}
                  className="bg-white text-[#0b1f4d] hover:bg-slate-100 px-6 sm:px-8 py-4 rounded-full text-base font-bold shadow-xl shadow-white/10 group"
                  data-testid="hero-represent-btn"
                >
                  REPRESENT YOUR COLLEGE
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  onClick={scrollToLeaderboard}
                  variant="outline"
                  className="border-2 border-white/20 text-white hover:bg-white/10 px-5 py-4 rounded-full text-sm font-semibold"
                  data-testid="explore-leaderboard-btn"
                >
                  <Trophy className="mr-2 w-4 h-4 text-amber-400" />
                  View Leaderboard
                </Button>
              </div>

              {/* Subtitle */}
              <p className="text-slate-400 text-sm">
                3 minutes • Get your score • Help your college rank
              </p>
            </div>

            {/* Right: Hero Image */}
            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect behind image */}
                <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 to-teal-500/20 rounded-3xl blur-2xl" />
                
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl shadow-black/50">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/d947f440-07f8-41e8-916c-6e89d8965b4e/images/5946140ced9a965f0d346bc4dcf4534d66134ae146ca08b23a6d1ac789a483b4.jpeg"
                    alt="Dehradun college students celebrating"
                    className="w-full max-w-md lg:max-w-lg object-cover"
                  />
                  
                  {/* Overlay badge */}
                  <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-md rounded-xl p-3 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                          <Trophy className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">Your College Could Be #1</p>
                          <p className="text-slate-400 text-xs">Rally your campus to the top</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                </div>

                {/* Floating stats badge */}
                <div className="absolute -top-3 -right-3 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  LIVE
                </div>
              </div>
            </div>
          </div>

          {/* Trust Strip */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 max-w-3xl mx-auto mt-10">
            {[
              { icon: Clock, text: "3-Min Check", color: "text-sky-400" },
              { icon: Target, text: "Instant Score", color: "text-emerald-400" },
              { icon: Building2, text: "College Ranking", color: "text-teal-400" },
              { icon: Award, text: "Verified Rewards", color: "text-amber-400" },
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 px-4 py-2.5 rounded-full"
              >
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-sm text-white/90 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          LIVE LEADERBOARD
          ============================================ */}
      <section ref={leaderboardRef} className="py-12 sm:py-16 px-4 bg-gradient-to-b from-slate-100 to-white">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-bold mb-4">
              <Trophy className="w-4 h-4" />
              LIVE LEADERBOARD
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Dehradun College Rankings
            </h2>
            <p className="text-slate-500 mt-2">
              {QUALIFICATION_THRESHOLD}+ students required to qualify for official ranking
            </p>
          </div>

          {/* Leaderboard Tabs */}
          <div className="flex gap-2 mb-6 justify-center">
            {[
              { id: 'challengers', label: 'Challengers', count: LEADERBOARD_DATA.challengers.length },
              { id: 'qualified', label: 'Qualified', count: LEADERBOARD_DATA.qualified.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setLeaderboardTab(tab.id)}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  leaderboardTab === tab.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
                data-testid={`leaderboard-tab-${tab.id}`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Leaderboard Content */}
          {leaderboardTab === 'qualified' && LEADERBOARD_DATA.qualified.length === 0 ? (
            <div className="bg-gradient-to-br from-teal-50 to-teal-50 border border-teal-200 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">The Race is On!</h3>
              <p className="text-slate-600 mb-4">
                No college has qualified yet. Be the first to rally{' '}
                <span className="font-bold text-teal-600">{QUALIFICATION_THRESHOLD} students</span>{' '}
                and claim the #1 spot!
              </p>
              <Button onClick={() => handleRepresentClick('leaderboard-empty')} className="bg-teal-600 hover:bg-teal-700">
                Start Your College's Journey
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {(leaderboardTab === 'qualified' ? LEADERBOARD_DATA.qualified : LEADERBOARD_DATA.challengers).map((row, idx) => (
                <div 
                  key={idx}
                  className={`bg-white rounded-xl border-2 p-4 sm:p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                    idx === 0 ? 'border-amber-300 shadow-amber-100' : 
                    idx === 1 ? 'border-slate-300' : 
                    idx === 2 ? 'border-orange-200' : 'border-slate-200'
                  }`}
                  data-testid={`leaderboard-row-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank Badge */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                      idx === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900' :
                      idx === 1 ? 'bg-gradient-to-br from-slate-200 to-slate-300 text-slate-800' :
                      idx === 2 ? 'bg-gradient-to-br from-amber-600 to-orange-500 text-white' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {idx === 0 && <Crown className="w-6 h-6" />}
                      {idx === 1 && <Medal className="w-6 h-6" />}
                      {idx === 2 && <Medal className="w-5 h-5" />}
                      {idx > 2 && `#${row.rank}`}
                    </div>

                    {/* College Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-900 truncate">{row.college}</h3>
                        <MovementBadge type={row.movement} />
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-500">
                            <span className="font-bold text-slate-800">{row.students}</span>/{QUALIFICATION_THRESHOLD} students
                          </span>
                          <span className="text-teal-600 font-semibold">
                            {QUALIFICATION_THRESHOLD - row.students} more to qualify!
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              row.progress >= 80 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                              row.progress >= 50 ? 'bg-gradient-to-r from-amber-400 to-orange-400' :
                              'bg-gradient-to-r from-teal-400 to-teal-400'
                            }`}
                            style={{ width: `${row.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Share Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-shrink-0 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => {
                        const text = `Help ${row.college} climb the Dehradun Career Readiness Index! Take the 3-min check: elevatecareersindia.in/dehradun`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      data-testid={`share-btn-${idx}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Boost
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-8">
            <p className="text-slate-500 mb-3">Don't see your college? Get them on the map!</p>
            <Button 
              onClick={() => handleRepresentClick('leaderboard')}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-semibold"
            >
              Add Your College
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================
          HOW IT WORKS
          ============================================ */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-3">
            How It Works
          </h2>
          <p className="text-slate-500 text-center mb-10">Five steps to put your college on the map</p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 sm:gap-3">
            {[
              { step: 1, icon: PlayCircle, title: "Take the Check", desc: "3-min diagnostic", color: "bg-teal-100 text-teal-600" },
              { step: 2, icon: Target, title: "Get Your Score", desc: "Instant results", color: "bg-emerald-100 text-emerald-600" },
              { step: 3, icon: Building2, title: "Represent College", desc: "Count towards Index", color: "bg-amber-100 text-amber-600" },
              { step: 4, icon: Share2, title: "Rally Classmates", desc: "Share on WhatsApp", color: "bg-teal-100 text-teal-600" },
              { step: 5, icon: Trophy, title: "Win Recognition", desc: "Campus Champion", color: "bg-rose-100 text-rose-600" },
            ].map((item, idx) => (
              <div key={idx} className="relative text-center group">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">{item.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
                
                {idx < 4 && (
                  <div className="hidden sm:block absolute top-10 -right-2 text-slate-300">
                    <ChevronRight className="w-5 h-5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          STUDENT REWARDS
          ============================================ */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <Award className="w-4 h-4" />
              REWARDS
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Represent. Refer. Get Recognised.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Target, title: "Personal Career Readiness Report", desc: "Know exactly where you stand with actionable insights", color: "from-teal-500 to-teal-500" },
              { icon: Star, title: "Campus Champion Badge", desc: "Get recognized as a leader driving your college's participation", color: "from-amber-500 to-orange-500" },
              { icon: Users, title: "Verified Referral Rewards", desc: "Earn recognition for every classmate you bring to the Index", color: "from-emerald-500 to-teal-500" },
              { icon: Trophy, title: "Top Contributor Recognition", desc: "Be featured as a top contributor when your college qualifies", color: "from-rose-500 to-pink-500" },
            ].map((item, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-600 mt-8 font-medium">
            Take the check. Share it with your campus. Help your college climb. 🚀
          </p>
        </div>
      </section>

      {/* ============================================
          CAMPUS CHAMPION CTA
          ============================================ */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-teal-600 via-teal-600 to-teal-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6">
            <Star className="w-10 h-10 text-amber-300" />
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold mb-4">
            Become a Campus Champion
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-xl mx-auto">
            Connected with students on your campus? Lead the charge. Rally your college. 
            Earn exclusive recognition as an official Campus Champion.
          </p>
          <Button 
            onClick={() => { trackEvent('campus_champion_click'); setShowCampusChampionModal(true); }}
            className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-full text-base font-bold shadow-xl"
            data-testid="campus-champion-btn"
          >
            I WANT TO BE A CAMPUS CHAMPION
            <ArrowUpRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* ============================================
          COLLEGE PARTNER SECTION
          ============================================ */}
      <section className="py-12 sm:py-16 px-4 bg-slate-100">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-slate-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Want Your College Officially Represented?
                </h2>
                <p className="text-slate-500 text-sm mt-1">For TPOs, Faculty & Academic Leaders</p>
              </div>
            </div>
            <p className="text-slate-600 mb-6">
              Get official participation status, receive aggregate insights on your students' 
              career readiness, and showcase your college's ranking on the Dehradun Index.
            </p>
            <Button 
              onClick={() => { trackEvent('college_partner_click'); setShowCollegePartnerModal(true); }}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold"
              data-testid="college-partner-btn"
            >
              GET YOUR COLLEGE INVOLVED
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================
          FAQ
          ============================================ */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-10">
            Frequently Asked Questions
          </h2>

          <div className="space-y-3">
            {faqData.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-100 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-semibold text-slate-800 pr-4">{faq.question}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-4 text-slate-600 text-sm">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA
          ============================================ */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-br from-slate-950 via-[#0b1f4d] to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-teal-500 rounded-full blur-[128px] opacity-30" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-500 rounded-full blur-[150px] opacity-20" />
        </div>
        
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-4 leading-tight tracking-tight">
            Your College. Your Voice.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
              Your Readiness.
            </span>
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Where will your campus rank?
          </p>
          
          <Button 
            onClick={() => handleRepresentClick('final')}
            className="bg-white text-[#0b1f4d] hover:bg-slate-100 px-10 py-6 rounded-full text-lg font-bold shadow-2xl shadow-white/10 group"
            data-testid="final-represent-btn"
          >
            REPRESENT YOUR COLLEGE
            <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <p className="text-slate-400 text-sm mt-6">
            3 minutes. Get your score. Help your college rank.
          </p>
        </div>
      </section>

      {/* ============================================
          FOOTER
          ============================================ */}
      <footer className="py-8 px-4 bg-slate-950 text-slate-400 text-sm">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© 2026 Elevate Careers India. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/contact" className="hover:text-white transition">Contact</Link>
          </div>
        </div>
      </footer>

      {/* ============================================
          STICKY MOBILE CTA
          ============================================ */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 sm:hidden z-40">
        <Button 
          onClick={() => handleRepresentClick('sticky')}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-xl font-bold"
          data-testid="sticky-cta-btn"
        >
          <Trophy className="w-5 h-5 mr-2" />
          Represent Your College
        </Button>
      </div>

      {/* ============================================
          MODALS
          ============================================ */}
      {showCampusChampionModal && (
        <CampusChampionModal onClose={() => setShowCampusChampionModal(false)} />
      )}
      {showCollegePartnerModal && (
        <CollegePartnerModal onClose={() => setShowCollegePartnerModal(false)} />
      )}

      {/* ============================================
          CUSTOM STYLES
          ============================================ */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================
// CAMPUS CHAMPION MODAL
// ============================================
const CampusChampionModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '', college: '', course_year: '', phone: '', email: '', social: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/dcri/campus-champion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      trackEvent('campus_champion_submitted');
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="campus-champion-modal">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Become a Campus Champion</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="modal-close-btn">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-slate-900 mb-2">You're In! 🎉</h4>
              <p className="text-slate-600">We'll reach out shortly with your Campus Champion kit.</p>
              <Button onClick={onClose} className="mt-6">Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full Name *" data-testid="champion-name-input" />
              <Input required value={formData.college} onChange={(e) => setFormData({ ...formData, college: e.target.value })} placeholder="College Name *" data-testid="champion-college-input" />
              <Input required value={formData.course_year} onChange={(e) => setFormData({ ...formData, course_year: e.target.value })} placeholder="Course & Year (e.g., B.Tech 3rd Year) *" data-testid="champion-course-input" />
              <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="WhatsApp Number *" data-testid="champion-phone-input" />
              <Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email *" data-testid="champion-email-input" />
              <Input value={formData.social} onChange={(e) => setFormData({ ...formData, social: e.target.value })} placeholder="Instagram / LinkedIn (optional)" data-testid="champion-social-input" />
              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 py-3" disabled={isSubmitting} data-testid="champion-submit-btn">
                {isSubmitting ? 'Submitting...' : 'Apply to be a Champion'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// COLLEGE PARTNER MODAL
// ============================================
const CollegePartnerModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '', institution: '', role: '', email: '', phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/dcri/college-partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      trackEvent('college_partner_submitted');
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="college-partner-modal">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Get Your College Involved</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="modal-close-btn">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h4>
              <p className="text-slate-600">Our team will contact you within 24 hours.</p>
              <Button onClick={onClose} className="mt-6">Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Your Name *" data-testid="partner-name-input" />
              <Input required value={formData.institution} onChange={(e) => setFormData({ ...formData, institution: e.target.value })} placeholder="Institution Name *" data-testid="partner-institution-input" />
              <Input required value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="Your Role (e.g., TPO, Dean) *" data-testid="partner-role-input" />
              <Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Official Email *" data-testid="partner-email-input" />
              <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Contact Number *" data-testid="partner-phone-input" />
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 py-3" disabled={isSubmitting} data-testid="partner-submit-btn">
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dehradun;
